import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { BookingCategory, FeatureFlag } from "PFTypes";
import { useEffect, useMemo } from "react";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BookingFormValues } from "./booking_form_provider";

type UseCategoryError = {
  categoryFormValueName: FieldPath<BookingFormValues>;
};
type UseCategoryErrorReturn = { getCategoryErrorMessage: (data: GetCategoryErrorMessage) => string };
type GetCategoryErrorMessage = {
  category?: BookingCategory | null;
  isDemandRelated: boolean;
};

const buildGetCategoryErrorMessage =
  (t: (key: string) => string, isBookingModuleEnabled: boolean) =>
  ({ category, isDemandRelated }: GetCategoryErrorMessage): string => {
    if (!category || !isBookingModuleEnabled) {
      return "";
    }
    if (isDemandRelated && !category.activity_assignable) {
      return t("bookings.create.categoryErrors.selectDemandRelatedCategory");
    }
    if (!isDemandRelated && category.activity_assignable) {
      return t("bookings.create.categoryErrors.selectNonDemandRelatedCategory");
    }
    return "";
  };

export const useCategoryErrorMessage = ({
  categoryFormValueName
}: UseCategoryError): UseCategoryErrorReturn => {
  const { t } = useTranslation("bookingModule");
  const isEnabled = useIsFeatureEnabled();
  const { setError, clearErrors } = useFormContext();
  const [activityId, category] = useWatch({ name: ["activityId", categoryFormValueName] });
  const isBookingModuleEnabled = isEnabled(FeatureFlag.BookingModule);
  const isDemandRelated = !!activityId;

  const getCategoryErrorMessage = buildGetCategoryErrorMessage(t, isBookingModuleEnabled);

  useEffect(() => {
    const message = getCategoryErrorMessage({ category, isDemandRelated });
    if (message) {
      setError(categoryFormValueName, {
        message
      });
    } else {
      clearErrors(categoryFormValueName);
    }
  }, [category, isDemandRelated]);

  return { getCategoryErrorMessage };
};

export const useGetCategoryErrorMessage = (): ((data: GetCategoryErrorMessage) => string) => {
  const { t } = useTranslation("bookingModule");
  const isBookingModuleEnabled = useIsFeatureEnabled()(FeatureFlag.BookingModule);

  return useMemo(() => buildGetCategoryErrorMessage(t, isBookingModuleEnabled), [t, isBookingModuleEnabled]);
};
